import React, { useState, useEffect, useCallback, useMemo, useRef } from "react";
import {
  ArrowLeft,
  FileText,
  CheckCircle,
  Clock,
  AlertCircle,
  Eye,
  DollarSign,
  Receipt,
  Calendar,
  Timer,
  Upload,
  FolderOpen,
  ChevronDown,
  ChevronRight,
  Download,
  Copy,
  Gift,
  Users,
  X,
  Loader2,
  CheckCircle2,
  Award,
  Star,
  Video,
  Coins,
  BadgeCheck,
  Search,
  RefreshCw,
  VideoOff,
} from "lucide-react";

import toast, { Toaster } from "react-hot-toast";
import StudentClassesModal from "./StudentClassesModal";
import RaiseInvoiceModal from "./RaiseInvoiceModal";
import { useAuth } from "../../contexts/AuthContext";
import { TrainerAPI } from "../../api/TrainerAPI";
import UploadResourceModal from "../UploadResourceModal";
import ViewResourcesModal from "../ViewResourcesModal";

const API_BASE =
  process.env.REACT_APP_API_BASE_URL || "http://localhost:5000/api";
const ITEMS_PER_PAGE = 10;

// The "Join" button is no longer gated by trainer email — it is enabled per
// batch based on whether that batch actually has a Google Meet link, since
// each batch is provisioned with its own meeting independently.
const hasMeetingLink = (batchItem) =>
  Boolean(batchItem?.meetingLink && String(batchItem.meetingLink).trim());

// Refer button becomes clickable only once 50% of MOU hours are attended
// for that specific batch. Admin's checkbox (canRefer) controls *visibility*
// of the button; this controls whether it's *enabled*.
const REFER_UNLOCK_RATIO = 0.5; // 50%

const getReferEligibility = (batchItem) => {
  const attended = parseFloat(batchItem?.attendedHours) || 0;
  const mouHours = parseFloat(batchItem?.mouHours) || 0;

  if (!mouHours || mouHours <= 0) {
    return { eligible: false, percent: 0 };
  }

  const percent = (attended / mouHours) * 100;
  return { eligible: percent >= REFER_UNLOCK_RATIO * 100, percent };
};
const getAuthHeaders = () => {
  const token = localStorage.getItem("authToken");
  return {
    "Content-Type": "application/json",
    ...(token && { Authorization: `Bearer ${token}` }),
  };
};

// ---- Course → Logo mapping ----
// Order matters: more specific keywords must come before generic ones
// (e.g. "aws security" before "aws").
const COURSE_LOGO_MAP = [
  ["aws security", "/course-logos/aws_security.svg"],
  ["azure security", "/course-logos/azure_security.svg"],
  ["microsoft cybersecurity", "/course-logos/microsoft_cybersecurity.svg"],
  ["ai in cybersecurity", "/course-logos/ai_in_cybersecurity.svg"],
  ["aigp", "/course-logos/aigp.svg"],
  ["artificial intelligence governance", "/course-logos/aigp.svg"],
  ["it auditing", "/course-logos/it_auditing_and_grc.svg"],
  ["grc", "/course-logos/it_auditing_and_grc.svg"],
  ["soc analyst", "/course-logos/soc_analyst.svg"],
  ["devops", "/course-logos/devops.svg"],
  ["aws", "/course-logos/aws.svg"],
  ["azure", "/course-logos/azure.svg"],
  ["data analytics", "/course-logos/data_analytics.svg"],
  ["project management", "/course-logos/project_management.svg"],
  ["project scheduler", "/course-logos/project_scheduler.svg"],
  ["ai & data science", "/course-logos/ai_and_data_science.svg"],
  ["ai and data science", "/course-logos/ai_and_data_science.svg"],
  ["python", "/course-logos/python.svg"],
  ["linux", "/course-logos/linux_system_administrator.svg"],
  ["agentic ai", "/course-logos/agentic_ai.svg"],
  ["data engineer", "/course-logos/data_engineer.svg"],
  ["cybersecurity", "/course-logos/cybersecurity.svg"], // generic fallback, keep last
];

const getCourseLogo = (courseName = "", fileName = "") => {
  const haystack = `${courseName} ${fileName}`
    .toLowerCase()
    .replace(/[_-]/g, " ")
    .replace(/&/g, "and")
    .replace(/\s+/g, " ")
    .trim();

  const match = COURSE_LOGO_MAP.find(([keyword]) => haystack.includes(keyword));
  return match ? match[1] : null;
};

const STATUS_STYLES = {
  active: "text-green-700 bg-green-100",
  "on-going": "text-green-700 bg-green-100",
  "in progress": "text-green-700 bg-green-100",
  completed: "text-blue-700 bg-blue-100",
  "not started": "text-yellow-700 bg-yellow-100",
};

const INVOICE_STATUS_STYLES = {
  submitted: "bg-yellow-100 text-yellow-800",
  approved: "bg-green-100 text-green-700",
  hold: "bg-red-100 text-red-600",
  "receipt sent": "bg-blue-100 text-blue-600",
  processing: "bg-indigo-100 text-indigo-600",
  "payment sent": "bg-green-200 text-green-800",
  "not submitted": "bg-gray-100 text-gray-600",
  open: "bg-gray-100 text-gray-600",
  unavailable: "bg-orange-50 text-orange-500",
};

const getStatusColor = (status) =>
  STATUS_STYLES[status?.toLowerCase()] || "text-gray-600 bg-gray-100";

// Statuses that share the "on-going" green styling above are treated as the
// same ongoing bucket and pinned above everything else in the batch list.
const ONGOING_STATUSES = new Set(["on-going", "active", "in progress"]);
const isOngoingStatus = (status) =>
  ONGOING_STATUSES.has(status?.toLowerCase()?.trim());

const getInvoiceStatusColor = (status) =>
  INVOICE_STATUS_STYLES[status?.toLowerCase()] || "bg-gray-100 text-gray-700";

const getInvoiceIcon = (status) => {
  switch (status?.toLowerCase()) {
    case "submitted":
      return <Clock className="w-4 h-4" />;
    case "approved":
      return <CheckCircle className="w-4 h-4" />;
    case "hold":
      return <AlertCircle className="w-4 h-4" />;
    case "receipt sent":
    case "payment sent":
      return <Receipt className="w-4 h-4" />;
    case "processing":
      return <FileText className="w-4 h-4" />;
    default:
      return <DollarSign className="w-4 h-4" />;
  }
};

const stripFileExtension = (name = "") =>
  name
    .replace(/\.[^/.]+$/, "") // Remove extension
    .replace(/[_-]/g, " ") // Replace _ and - with spaces
    .replace(/&/g, "&") // Keep &
    .replace(/\s+/g, " ") // Remove extra spaces
    .trim();

const StepBadge = ({ n }) => (
  <span className="w-6 h-6 rounded-full bg-[#221E45] text-white text-xs font-bold flex items-center justify-center shrink-0">
    {n}
  </span>
);

// ---------------- Tooltip ----------------
const Tooltip = ({ children, content, position = "top" }) => {
  const [isVisible, setIsVisible] = useState(false);

  const positionClasses = {
    top: "bottom-full left-1/2 -translate-x-1/2 mb-2",
    bottom: "top-full left-1/2 -translate-x-1/2 mt-2",
    left: "right-full top-1/2 -translate-y-1/2 mr-2",
    right: "left-full top-1/2 -translate-y-1/2 ml-2",
  };

  const arrowClasses = {
    top: "top-full left-1/2 -translate-x-1/2 -mt-1",
    bottom: "bottom-full left-1/2 -translate-x-1/2 -mb-1",
    left: "left-full top-1/2 -translate-y-1/2 -ml-1",
    right: "right-full top-1/2 -translate-y-1/2 -mr-1",
  };

  return (
    <div className="relative inline-block">
      <div
        onMouseEnter={() => setIsVisible(true)}
        onMouseLeave={() => setIsVisible(false)}
      >
        {children}
      </div>
      {isVisible && (
        <div
          className={`absolute z-10 px-3 py-2 text-xs font-medium text-white bg-gray-900 rounded-lg shadow-lg whitespace-nowrap ${positionClasses[position]}`}
        >
          {content}
          <div
            className={`absolute w-2 h-2 bg-gray-900 transform rotate-45 ${arrowClasses[position]}`}
          />
        </div>
      )}
    </div>
  );
};

// ---------------- Resource Actions Dropdown ----------------
const ResourceActionsDropdown = ({ onUpload, onView, onCopy, canUpload = true }) => {
  const [isOpen, setIsOpen] = useState(false);

  return (
    <div
      className="relative inline-flex items-center gap-2"
      style={{ zIndex: isOpen ? 1000 : 1 }}
    >
      <button
        onClick={(e) => {
          e.stopPropagation();
          setIsOpen((prev) => !prev);
        }}
        className="flex items-center gap-1.5 px-3 py-1.5 bg-indigo-600 text-white rounded-lg hover:bg-indigo-700 transition-all text-xs font-medium shadow-sm"
      >
        <FolderOpen className="w-3.5 h-3.5" />
        {/* <span>Resources</span> */}
        <ChevronDown
          className={`w-3 h-3 transition-transform duration-200 ${isOpen ? "rotate-180" : ""}`}
        />
      </button>

      <button
        onClick={(e) => {
          e.stopPropagation();
          onCopy();
        }}
        title="Copy resource link"
        className="p-1.5 rounded-lg border border-gray-200 bg-white hover:bg-blue-50 hover:border-blue-300 transition-colors"
      >
        <Copy className="w-3.5 h-3.5 text-blue-500" />
      </button>

      {isOpen && (
        <>
          <div
            className="fixed inset-0"
            style={{ zIndex: 9998 }}
            onClick={() => setIsOpen(false)}
          />
          <div
            className="absolute left-0 top-full mt-1.5 w-44 bg-white rounded-lg shadow-xl border border-gray-200 py-1"
            style={{ zIndex: 9999 }}
          >
            {canUpload && (
              <button
                onClick={(e) => {
                  e.stopPropagation();
                  onUpload();
                  setIsOpen(false);
                }}
                className="w-full px-4 py-2.5 text-left text-sm text-gray-700 hover:bg-indigo-50 hover:text-indigo-700 transition-colors flex items-center gap-3 group"
              >
                <div className="p-1.5 bg-indigo-100 rounded group-hover:bg-indigo-200 transition-colors">
                  <Upload className="w-3.5 h-3.5 text-indigo-600" />
                </div>
                <span className="font-medium">Upload Files</span>
              </button>
            )}
            <button
              onClick={(e) => {
                e.stopPropagation();
                onView();
                setIsOpen(false);
              }}
              className="w-full px-4 py-2.5 text-left text-sm text-gray-700 hover:bg-green-50 hover:text-green-700 transition-colors flex items-center gap-3 group"
            >
              <div className="p-1.5 bg-green-100 rounded group-hover:bg-green-200 transition-colors">
                <Eye className="w-3.5 h-3.5 text-green-600" />
              </div>
              <span className="font-medium">View Files</span>
            </button>
          </div>
        </>
      )}
    </div>
  );
};

// ---------------- Refer & Earn Banner ----------------
const ReferEarnBanner = ({
  loadingReferrals,
  totalEarnedPoints,
  onOpenRedemptions,
}) => (
  <div className="flex flex-col sm:flex-row items-stretch gap-0 rounded-xl overflow-hidden shadow-sm mb-4 border border-gray-200">
    {/* Left: gradient banner */}
    <div className="flex items-center gap-3 px-5 py-3 bg-gradient-to-r from-[#EF7467] via-[#C7A7AD] to-[#282046] flex-1 min-w-0">
      <div className="relative shrink-0">
        <div className="w-10 h-10 rounded-full bg-gray-600 flex items-center justify-center">
          <Users className="w-5 h-5 text-white" />
        </div>
        <div className="absolute -top-1 -right-1 w-4 h-4 rounded-full bg-orange-400 flex items-center justify-center">
          <span className="text-white text-[9px] font-bold">+</span>
        </div>
      </div>
      <div>
        <p className="text-white font-bold text-base leading-tight">
          Refer &amp; Earn
        </p>
        <p className="text-gray-300 text-xs mt-0.5">
          Get 20 points on every referral
        </p>
      </div>
    </div>

    {/* Divider */}
    <div className="hidden sm:block w-px bg-gray-200" />

    {/* Right: stats */}
    <div className="flex divide-x divide-gray-200 bg-white">
      {/* Total Earned Points */}
      <div className="flex items-center gap-2.5 px-5 py-3 flex-1 min-w-[150px]">
        <div className="w-8 h-8 rounded-full bg-orange-50 border-2 border-orange-200 flex items-center justify-center shrink-0">
          <Star className="w-4 h-4 text-orange-400" />
        </div>
        <div>
          <p className="text-[10px] font-semibold text-orange-500 uppercase tracking-wide">
            Points
          </p>
          <p className="text-xl font-bold text-gray-900 leading-tight">
            {loadingReferrals ? "—" : totalEarnedPoints}
          </p>
        </div>
        <Award className="w-4 h-4 text-gray-300 ml-auto" />
      </div>

      {/* Available Redemptions trigger button */}
      <button
        onClick={onOpenRedemptions}
        className="flex items-center gap-2 px-5 py-3 text-sm font-medium text-[#FC7A69] hover:bg-orange-50 transition-colors whitespace-nowrap"
      >
        <Gift className="w-4 h-4" />
        Available Redemptions
      </button>
    </div>
  </div>
);

// ---------------- Redemption Modal — real API data ----------------
const RedemptionModal = ({ isOpen, onClose }) => {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [redeeming, setRedeeming] = useState(false);
  const [error, setError] = useState("");
  const [successMsg, setSuccessMsg] = useState("");

  const POINTS_PER_BLOCK = 100;
  const AMOUNT_PER_BLOCK = 500;

  const fetchRedemptions = async () => {
    setLoading(true);
    setError("");
    try {
      const res = await fetch(`${API_BASE}/trainer/referrals/redemptions`, {
        headers: getAuthHeaders(),
      });
      const json = await res.json();
      if (!res.ok) throw new Error(json.error || "Failed to load redemptions");
      setData(json.data);
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (isOpen) {
      fetchRedemptions();
      setSuccessMsg("");
      setError("");
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen]);

  const handleRedeem = async () => {
    setRedeeming(true);
    setSuccessMsg("");
    setError("");
    try {
      const res = await fetch(
        `${API_BASE}/trainer/referrals/redemptions/redeem`,
        {
          method: "POST",
          headers: getAuthHeaders(),
        },
      );
      const json = await res.json();
      if (!res.ok) throw new Error(json.error || "Redemption failed");
      setSuccessMsg("Redemption request submitted successfully!");
      toast.success("Redemption request submitted!");
      fetchRedemptions();
    } catch (err) {
      setError(err.message);
    } finally {
      setRedeeming(false);
    }
  };

  if (!isOpen) return null;
  const earnedPoints = data?.earned_reward_points || 0;
  const history = data?.redemption_history || [];
  const fullBlocks = Math.floor(earnedPoints / POINTS_PER_BLOCK);
  const remainder = earnedPoints % POINTS_PER_BLOCK;

  const rows = [];
  for (let i = 0; i < fullBlocks; i++) {
    const record = history[i];
    rows.push({
      points: POINTS_PER_BLOCK,
      amount: AMOUNT_PER_BLOCK,
      redeemed: !!record,
      status: record?.redemption_status || "unpaid",
    });
  }
  if (remainder > 0) {
    rows.push({
      points: remainder,
      amount: remainder * 5, // ₹5 per point, same rate as 100→500
      redeemed: false,
      status: "unpaid",
      partial: true, // always disabled
    });
  }

  return (
    <div className="fixed inset-0 bg-black/40 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-xl shadow-xl border border-gray-200 p-5 w-full max-w-lg max-h-[85vh] overflow-y-auto">
        {/* Header */}
        <div className="flex items-center justify-between mb-4">
          <div className="flex items-center gap-2">
            <div className="w-7 h-7 bg-[#FC7A69]/10 rounded-lg flex items-center justify-center">
              <Coins className="w-4 h-4 text-[#FC7A69]" />
            </div>
            <h2 className="text-sm font-semibold text-[#221E45]">
              Available Redemptions
            </h2>
          </div>
          <button
            onClick={onClose}
            className="text-gray-400 hover:text-gray-600"
            aria-label="Close"
          >
            <X className="w-5 h-5" />
          </button>
        </div>

        {loading ? (
          <div className="flex flex-col items-center justify-center py-10 text-gray-400">
            <Loader2 className="w-6 h-6 animate-spin mb-2" />
            <p className="text-sm">Loading…</p>
          </div>
        ) : error ? (
          <div className="p-3 rounded-lg bg-red-50 text-red-700 text-xs flex items-center gap-2">
            <AlertCircle className="w-4 h-4 shrink-0" /> {error}
          </div>
        ) : (
          <>
            {successMsg && (
              <div className="mb-3 p-3 rounded-lg bg-green-50 text-green-700 text-xs flex items-center gap-2">
                <CheckCircle2 className="w-4 h-4 shrink-0" /> {successMsg}
              </div>
            )}

            {/* Redemption rows */}
            <div className="space-y-2">
              {rows.length === 0 ? (
                <p className="text-sm text-gray-400 text-center py-6">
                  No points earned yet.
                </p>
              ) : (
                rows.map((row, idx) => (
                  <div
                    key={idx}
                    className="flex items-center justify-between px-4 py-3 border border-gray-200 rounded-lg"
                  >
                    <span className="text-sm font-medium text-gray-700">
                      {row.points} pts &nbsp;→&nbsp; ₹{row.amount}
                    </span>

                    <button
                      onClick={handleRedeem}
                      disabled={row.redeemed || row.partial || redeeming}
                      className={`px-4 py-1.5 rounded-lg text-xs font-medium transition-all ${
                        row.redeemed || row.partial
                          ? "bg-gray-100 text-gray-400 cursor-not-allowed"
                          : "bg-[#FC7A69] text-white hover:bg-[#fb6a57]"
                      }`}
                    >
                      {row.redeemed
                        ? "Redeemed"
                        : redeeming
                          ? "Redeeming…"
                          : "Redeem"}
                    </button>

                    <span
                      className={`px-2 py-0.5 rounded-full text-[10px] font-medium border ${
                        row.status === "paid"
                          ? "bg-green-50 text-green-700 border-green-200"
                          : row.redeemed
                            ? "bg-yellow-50 text-yellow-700 border-yellow-200"
                            : "bg-gray-100 text-gray-500 border-gray-200"
                      }`}
                    >
                      {row.status === "paid"
                        ? "Paid"
                        : row.redeemed
                          ? "Unpaid"
                          : "Pending"}
                    </span>
                  </div>
                ))
              )}
            </div>
          </>
        )}
      </div>
    </div>
  );
};

// ---------------- Templates Grid Modal — "Get Templates" popup ----------------
const TemplatesModal = ({
  isOpen,
  onClose,
  templates,
  loading,
  error,
  onSelect,
}) => {
  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-50 bg-white flex flex-col w-screen h-screen overflow-hidden">
      <div className="bg-white w-full h-full flex flex-col">
        {/* Header */}
        <div className="flex items-center justify-between px-5 py-4 border-b border-gray-200 shrink-0">
          <div className="flex items-center gap-2">
            <div className="w-7 h-7 bg-[#FC7A69]/10 rounded-lg flex items-center justify-center">
              <FileText className="w-4 h-4 text-[#FC7A69]" />
            </div>
            <h2 className="text-sm font-semibold text-[#221E45]">
              PPT Templates
            </h2>
          </div>
          <button
            onClick={onClose}
            className="text-gray-400 hover:text-gray-600"
            aria-label="Close"
          >
            <X className="w-5 h-5" />
          </button>
        </div>

        {/* Body — scrollable grid */}
        <div className="flex-1 overflow-y-auto px-5 py-4">
          {loading ? (
            <div className="flex flex-col items-center justify-center py-14 text-gray-400">
              <Loader2 className="w-6 h-6 animate-spin mb-2" />
              <p className="text-sm">Loading templates…</p>
            </div>
          ) : error ? (
            <div className="p-3 rounded-lg bg-red-50 text-red-700 text-xs flex items-center gap-2">
              <AlertCircle className="w-4 h-4 shrink-0" /> {error}
            </div>
          ) : templates.length === 0 ? (
            <p className="text-sm text-gray-400 text-center py-14">
              No templates available.
            </p>
          ) : (
            <div className="grid grid-cols-4 gap-3">
              {templates.map((tpl) => {
                const logo = getCourseLogo("", tpl.fileName);

                return (
                  <button
                    key={tpl.key}
                    onClick={() => onSelect(tpl)}
                    className="flex flex-col items-center gap-2 p-3 border border-gray-200 rounded-lg hover:border-[#FC7A69] hover:bg-[#FC7A69]/5 transition-colors text-center"
                    title={tpl.fileName}
                  >
                    <div className="w-12 h-12 rounded-lg bg-indigo-50 flex items-center justify-center shrink-0">
                      {logo ? (
                        <img
                          src={logo}
                          alt={tpl.fileName}
                          className="w-20 h-20 object-contain"
                        />
                      ) : (
                        <FileText className="w-6 h-6 text-indigo-500" />
                      )}
                    </div>

                    <span className="text-xs font-medium text-gray-700 line-clamp-2 break-all">
                      {stripFileExtension(tpl.fileName)}
                    </span>
                  </button>
                );
              })}
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

// ---------------- Mobile Card ----------------
const MobileBatchCard = ({
  batchItem,
  onSelect,
  onUpload,
  onView,
  onCopy,
  onRefer,
  canRefer,
  joiningBatch,
  onJoin,
  isReferred,
  isPrevious = false,
}) => {
  const canJoin = !isPrevious && hasMeetingLink(batchItem);

  return (
    <div className="bg-white border border-gray-200 rounded-lg p-4 mb-4 shadow-sm hover:shadow-md transition-shadow">
      <div className="flex items-center justify-between mb-3">
        <button
          onClick={(e) => {
            e.stopPropagation();
            onSelect(batchItem);
          }}
          className="text-[#FC7A69] hover:text-[#221E45] font-bold text-lg hover:underline transition-colors"
        >
          #{batchItem.batch_no}
        </button>
        <span
          className={`px-2 py-1 rounded-full text-xs font-medium ${getStatusColor(batchItem.batchStatus)}`}
        >
          {batchItem.batchStatus || "Not Started"}
        </span>
      </div>
      {isPrevious && batchItem.currentTrainerName && (
        <p className="text-xs text-gray-400 mb-2">
          Now trained by {batchItem.currentTrainerName}
        </p>
      )}
      <h3 className="font-semibold text-gray-900 text-base mb-3 line-clamp-2">
        {batchItem.courseName}
      </h3>
      <div className="grid grid-cols-2 gap-4 mb-4">
        <div className="flex items-center">
          <Calendar className="w-4 h-4 text-gray-500 mr-2" />
          <div>
            <div className="text-xs text-gray-500">Start Date</div>
            <div className="font-semibold text-sm">
              {batchItem.startDate
                ? new Date(batchItem.startDate).toLocaleDateString("en-US", {
                    month: "short",
                    day: "numeric",
                  })
                : "N/A"}
            </div>
          </div>
        </div>
        <div className="flex items-center">
          <Timer className="w-4 h-4 text-gray-500 mr-2" />
          <div>
            <div className="text-xs text-gray-500">Batch Timing</div>
            <div className="font-semibold text-sm">
              {batchItem.batchTiming || "N/A"}
            </div>
          </div>
        </div>
      </div>
      <div className="flex items-center justify-between mb-4">
        <div className="flex items-center gap-2">
          <span className="text-xs text-gray-500">Invoice:</span>
          <span
            className={`px-2 py-1 rounded-full text-xs font-medium ${getInvoiceStatusColor(batchItem.status)}`}
          >
            {batchItem.status || "Not Submitted"}
          </span>
        </div>
        <Tooltip
          content={`Current Status: ${batchItem.status || "Not Submitted"}`}
        >
          <Eye className="w-4 h-4 text-gray-400 hover:text-gray-600 cursor-help" />
        </Tooltip>
      </div>
      <div className="pt-3 border-t border-gray-100 flex flex-col gap-2">
        <ResourceActionsDropdown
          onUpload={onUpload}
          onView={onView}
          onCopy={onCopy}
          canUpload={!isPrevious}
        />
        {isPrevious ? (
          <p className="text-xs text-gray-400 text-center py-1">
            Join &amp; Refer aren't available — you're no longer the assigned trainer
          </p>
        ) : (
          <>
            {canRefer &&
              (() => {
                const { eligible, percent } = getReferEligibility(batchItem);
                const disabled = isReferred || !eligible;
                return (
                  <div>
                    <button
                      onClick={(e) => {
                        e.stopPropagation();
                        if (!disabled) onRefer(batchItem);
                      }}
                      disabled={disabled}
                      className={`w-full flex items-center justify-center px-4 py-2.5 text-sm font-medium rounded-lg shadow-sm transition-all ${
                        disabled
                          ? "bg-gray-200 text-gray-400 cursor-not-allowed"
                          : "bg-[#FC7A69] text-white hover:bg-[#fb6a57]"
                      }`}
                    >
                      <Gift className="w-4 h-4 mr-2" />
                      {isReferred ? "Referred" : "Refer"}
                    </button>
                    {!isReferred && !eligible && (
                      <p className="text-[10px] text-gray-400 text-center mt-1">
                        Unlocks at 50% attendance ({percent.toFixed(0)}% now)
                      </p>
                    )}
                  </div>
                );
              })()}
            {canJoin ? (
              <button
                onClick={(e) => {
                  e.stopPropagation();
                  onJoin(batchItem);
                }}
                disabled={joiningBatch === batchItem.batch_no}
                className="w-full flex items-center justify-center gap-2 px-4 py-2.5 bg-gradient-to-r from-green-500 to-emerald-600 text-white rounded-lg hover:from-green-600 hover:to-emerald-700 transition-all duration-200 shadow-sm hover:shadow-md text-sm font-medium disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {joiningBatch === batchItem.batch_no ? (
                  <Loader2 className="w-4 h-4 animate-spin" />
                ) : (
                  <Video className="w-4 h-4" />
                )}
                <span>
                  {joiningBatch === batchItem.batch_no ? "Joining..." : "Join Class"}
                </span>
              </button>
            ) : (
              <button
                disabled
                className="w-full flex items-center justify-center gap-2 px-4 py-2.5 bg-gray-100 text-gray-400 rounded-lg text-sm font-medium cursor-not-allowed"
              >
                <VideoOff className="w-4 h-4" />
                <span>No Meeting Link</span>
              </button>
            )}
          </>
        )}
      </div>
    </div>
  );
};

// ---------------- Desktop Table Row ----------------
const DesktopRow = ({
  batchItem,
  onRowClick,
  onSelect,
  onUpload,
  onView,
  onCopy,
  onRefer,
  canRefer,
  joiningBatch,
  onJoin,
  isReferred,
  isPrevious = false,
}) => {
  const canJoin = !isPrevious && hasMeetingLink(batchItem);

  return (
    <tr
      className="hover:bg-gray-50 cursor-pointer"
      onClick={() => onRowClick(batchItem)}
    >
      <td className="border-b border-gray-100 px-4 py-3 text-xs">
        <button
          onClick={(e) => {
            e.stopPropagation();
            onSelect(batchItem);
          }}
          className="text-[#FC7A69] hover:text-[#221E45] hover:underline font-semibold transition-colors"
          title="Click to view batch details"
        >
          {batchItem.batch_no}
        </button>
      </td>
      <td className="border-b border-gray-100 px-4 py-3 text-xs text-gray-800">
        {batchItem.courseName}
      </td>
      <td className="border-b border-gray-100 px-4 py-3 text-xs">
        <div className="flex items-center gap-1.5">
          {isPrevious ? (
            <Tooltip
              content={
                batchItem.currentTrainerName
                  ? `Now trained by ${batchItem.currentTrainerName}`
                  : "Reassigned to another trainer"
              }
            >
              <span
                className={`px-2.5 py-1 rounded-full text-xs font-medium cursor-help ${getStatusColor(batchItem.batchStatus)}`}
              >
                {batchItem.batchStatus || "Not Started"}
              </span>
            </Tooltip>
          ) : (
            <span
              className={`px-2.5 py-1 rounded-full text-xs font-medium ${getStatusColor(batchItem.batchStatus)}`}
            >
              {batchItem.batchStatus || "Not Started"}
            </span>
          )}
        </div>
      </td>
      <td className="border-b border-gray-100 px-4 py-3 text-xs text-gray-700">
        {batchItem.startDate
          ? new Date(batchItem.startDate).toLocaleDateString()
          : "N/A"}
      </td>
      <td className="border-b border-gray-100 px-4 py-3 text-xs text-gray-700">
        {batchItem.batchTiming || "N/A"}
      </td>
      <td className="border-b border-gray-100 px-4 py-3 text-xs">
        <div className="flex items-center gap-2">
          <span
            className={`px-2.5 py-1 rounded-full text-xs font-medium ${getInvoiceStatusColor(batchItem.status)}`}
          >
            {batchItem.status || "Not Submitted"}
          </span>
          <Tooltip
            content={`Current Status: ${batchItem.status || "Not Submitted"}`}
          >
            <Eye className="w-3.5 h-3.5 text-gray-400 hover:text-gray-600 cursor-help" />
          </Tooltip>
        </div>
      </td>
      <td
        className="border-b border-gray-100 px-4 py-3 text-xs"
        style={{ overflow: "visible", position: "relative" }}
      >
        <ResourceActionsDropdown
          onUpload={onUpload}
          onView={onView}
          onCopy={onCopy}
          canUpload={!isPrevious}
        />
      </td>
      {canRefer && (
        <td className="border-b border-gray-100 px-4 py-3 text-xs">
          {isPrevious ? (
            <span className="text-gray-400">—</span>
          ) : (
            (() => {
              const { eligible, percent } = getReferEligibility(batchItem);
              const disabled = isReferred || !eligible;
              return (
                <Tooltip
                  content={
                    isReferred
                      ? "Already referred"
                      : eligible
                        ? "Refer this batch"
                        : `Unlocks at 50% attendance — currently ${percent.toFixed(0)}%`
                  }
                >
                  <button
                    onClick={(e) => {
                      e.stopPropagation();
                      if (!disabled) onRefer(batchItem);
                    }}
                    disabled={disabled}
                    className={`flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium rounded-lg shadow-sm transition-all ${
                      disabled
                        ? "bg-gray-200 text-gray-400 cursor-not-allowed"
                        : "bg-[#FC7A69] text-white hover:bg-[#fb6a57]"
                    }`}
                  >
                    <Gift className="w-3.5 h-3.5" />
                    {isReferred ? "Referred" : "Refer"}
                  </button>
                </Tooltip>
              );
            })()
          )}
        </td>
      )}
      <td className="border-b border-gray-100 px-4 py-3 text-xs text-center">
        {isPrevious ? (
          <span className="inline-flex items-center gap-1.5 px-3 py-1.5 bg-gray-100 text-gray-400 rounded-lg text-xs font-medium">
            Reassigned
          </span>
        ) : canJoin ? (
          <button
            onClick={(e) => {
              e.stopPropagation();
              onJoin(batchItem);
            }}
            disabled={joiningBatch === batchItem.batch_no}
            className="inline-flex items-center gap-1.5 px-3 py-1.5 bg-gradient-to-r from-green-500 to-emerald-600 text-white rounded-lg hover:from-green-600 hover:to-emerald-700 transition-all duration-200 shadow-sm hover:shadow-md text-xs font-medium disabled:opacity-50 disabled:cursor-not-allowed"
            title="Join Google Meet session"
          >
            {joiningBatch === batchItem.batch_no ? (
              <Loader2 className="w-3.5 h-3.5 animate-spin" />
            ) : (
              <Video className="w-3.5 h-3.5" />
            )}
            {joiningBatch === batchItem.batch_no ? "Joining..." : "Join"}
          </button>
        ) : (
          <span className="inline-flex items-center gap-1.5 px-3 py-1.5 bg-gray-100 text-gray-400 rounded-lg text-xs font-medium cursor-not-allowed">
            <VideoOff className="w-3.5 h-3.5" />
            No Link
          </span>
        )}
      </td>
    </tr>
  );
};

// ==================================================================
// Main Component
// ==================================================================
const BatchTableView = ({ onBack, onBatchClick = () => {} }) => {
  const { user } = useAuth();

  const [batches, setBatchesState] = useState([]);
  const [loadingBatches, setLoadingBatches] = useState(true);

  // "Current Batches" vs "Previous Batches" tabs — previous batches are
  // fetched lazily (only once the tab is opened) since finding them requires
  // a full scan server-side, unlike the Query-backed current batch list.
  const [activeTab, setActiveTab] = useState("current");
  const [previousBatches, setPreviousBatchesState] = useState([]);
  const [loadingPreviousBatches, setLoadingPreviousBatches] = useState(false);
  const [previousBatchesLoaded, setPreviousBatchesLoaded] = useState(false);
  const [previousBatchesError, setPreviousBatchesError] = useState("");

  const [selectedBatchStudents, setSelectedBatchStudents] = useState(null);
  const [selectedBatchNo, setSelectedBatchNo] = useState("");
  const [selectedBatchDetails, setSelectedBatchDetails] = useState(null);
  // Guards the journey-status overlay below against a slow response landing
  // after the trainer has already clicked into a different batch.
  const selectedBatchNoRef = useRef("");
  const [selectedBatchForInvoice, setSelectedBatchForInvoice] = useState(null);

  const [currentPage, setCurrentPage] = useState(1);

  // Batch No filter — free-text search against batch_no, applied client-side
  // on top of whichever tab's list is active.
  const [batchNoSearch, setBatchNoSearch] = useState("");

  const [openUploadModal, setOpenUploadModal] = useState(false);
  const [selectedBatchForUpload, setSelectedBatchForUpload] = useState(null);
  const [openViewModal, setOpenViewModal] = useState(false);
  const [selectedBatchForView, setSelectedBatchForView] = useState(null);

  const [loadingReferrals, setLoadingReferrals] = useState(true);
  const [totalEarnedPoints, setTotalEarnedPoints] = useState(0);
  const [showReferForm, setShowReferForm] = useState(false);
  const [activeReferBatch, setActiveReferBatch] = useState(null);
  const [referralDate, setReferralDate] = useState(
    () => new Date().toISOString().split("T")[0],
  );
  const [referralTimestamp, setReferralTimestamp] = useState(() =>
    new Date().toTimeString().slice(0, 5),
  );
  const [submittingReferral, setSubmittingReferral] = useState(false);
  const [referError, setReferError] = useState("");
  const [referSuccessMsg, setReferSuccessMsg] = useState("");

  // Template search/preview state — Refer modal Step 1
  const [templateSearch, setTemplateSearch] = useState("");
  const [templateResults, setTemplateResults] = useState([]);
  const [searchingTemplates, setSearchingTemplates] = useState(false);
  const [templateError, setTemplateError] = useState("");
  const [selectedTemplate, setSelectedTemplate] = useState(null);
  // Course Name (Step 4) — pre-filled from the selected template, editable
  const [courseName, setCourseName] = useState("");
  const [previewUrl, setPreviewUrl] = useState("");
  const [loadingPreview, setLoadingPreview] = useState(false);
  const [downloadingTemplate, setDownloadingTemplate] = useState(false);
  const [showPreviewPanel, setShowPreviewPanel] = useState(false); // collapsed by default to save vertical space

  // "Get Templates" grid popup state
  const [showTemplatesModal, setShowTemplatesModal] = useState(false);
  const [allTemplates, setAllTemplates] = useState([]);
  const [loadingAllTemplates, setLoadingAllTemplates] = useState(false);
  const [allTemplatesError, setAllTemplatesError] = useState("");

  // Batches this trainer has already referred (batch_no -> disables the Refer button)
  const [referredBatchNos, setReferredBatchNos] = useState(new Set());

  // Redemptions popup state
  const [showRedemptionModal, setShowRedemptionModal] = useState(false);

  // Google Meet Join state
  const [joiningBatch, setJoiningBatch] = useState(null);

  // Manual "Refresh" — lets a trainer re-fetch batches when a meeting link
  // was provisioned after the page first loaded (so Join wasn't shown yet).
  const [refreshing, setRefreshing] = useState(false);

  // Refer & Earn access gate
  const canRefer = user?.allowed_referral === "yes";

  // The list backing whichever tab is active — pagination, counts, and the
  // table/card rendering below all read from this rather than branching on
  // activeTab everywhere.
  const displayedBatches = activeTab === "previous" ? previousBatches : batches;
  const isPreviousTab = activeTab === "previous";

  // Batch No filter + "on-going first" sort, applied on top of whichever
  // tab's list is active. Filtering/sorting happens client-side since the
  // full list for the active tab is already in memory.
  const filteredSortedBatches = useMemo(() => {
    const query = batchNoSearch.trim().toLowerCase();
    const filtered = query
      ? displayedBatches.filter((b) =>
          b.batch_no?.toString().toLowerCase().includes(query),
        )
      : displayedBatches;

    // Stable sort — On-going batches first, original relative order preserved
    // within each group.
    return filtered
      .map((batch, index) => ({ batch, index }))
      .sort((a, b) => {
        const aOngoing = isOngoingStatus(a.batch.batchStatus) ? 0 : 1;
        const bOngoing = isOngoingStatus(b.batch.batchStatus) ? 0 : 1;
        return aOngoing - bOngoing || a.index - b.index;
      })
      .map(({ batch }) => batch);
  }, [displayedBatches, batchNoSearch]);

  const totalPages = Math.ceil(
    filteredSortedBatches.length / ITEMS_PER_PAGE,
  );
  const startIndex = (currentPage - 1) * ITEMS_PER_PAGE;
  const endIndex = startIndex + ITEMS_PER_PAGE;
  const currentBatches = useMemo(
    () => filteredSortedBatches.slice(startIndex, endIndex),
    [filteredSortedBatches, startIndex, endIndex],
  );

  const handleBatchNoSearchChange = useCallback((e) => {
    setBatchNoSearch(e.target.value);
    setCurrentPage(1);
  }, []);

  // `invoices` is `null` when the Invoice Service couldn't be reached — in that
  // case every batch gets "Unavailable" rather than being mismatched against
  // an empty list (which would otherwise read as "Not Submitted", a false claim).
  const mergeBatchesWithInvoiceStatus = useCallback((batchList, invoices) => {
    return batchList.map((b) => {
      if (!Array.isArray(invoices)) {
        return { ...b, status: "Unavailable" };
      }
      const batchNo = b.batch_no?.toString();
      const matchedInvoice = invoices.find((inv) => {
        const batchNumbers = [
          inv.batchNumber1,
          inv.batchNumber2,
          inv.batchNumber3,
        ].map((n) => n?.toString());
        return batchNumbers.includes(batchNo);
      });
      return { ...b, status: matchedInvoice?.status || "Not Submitted" };
    });
  }, []);

  // Batches (DynamoDB-backed) and invoice status (proxied through a separate
  // Invoice Service) are fetched independently so an Invoice Service outage
  // never prevents the batch list itself from loading — invoice status is
  // strictly a cosmetic enrichment on top of the batch data.
  // Returns true/false so callers (e.g. the manual Refresh button) can tell
  // whether the fetch actually succeeded, instead of assuming it always did.
  const loadBatchesAndInvoices = useCallback(async () => {
    if (!user?.email) return false;
    try {
      const [batchesResult, invoicesResult] = await Promise.allSettled([
        TrainerAPI.getBatches(),
        TrainerAPI.getTrainerInvoices(),
      ]);

      if (batchesResult.status === "rejected") {
        console.error("Error loading batches:", batchesResult.reason);
        // Deliberately leave any previously-loaded batches on screen rather
        // than clearing them — a transient failure on refresh shouldn't
        // wipe out data the trainer could already see.
        return false;
      }

      if (invoicesResult.status === "rejected") {
        console.error(
          "Invoice Service unavailable — showing batches without invoice status:",
          invoicesResult.reason
        );
      }

      const invoices =
        invoicesResult.status === "fulfilled" ? invoicesResult.value : null;

      setBatchesState(
        mergeBatchesWithInvoiceStatus(batchesResult.value, invoices)
      );
      return true;
    } catch (err) {
      console.error("Error loading batches or invoice status:", err);
      return false;
    } finally {
      setLoadingBatches(false);
    }
  }, [user, mergeBatchesWithInvoiceStatus]);

  useEffect(() => {
    loadBatchesAndInvoices();
  }, [loadBatchesAndInvoices]);

  // Previous batches are fetched once, the first time that tab is opened
  // (not on mount) — this is the scan-backed endpoint, so it's kept off the
  // page's initial load entirely.
  const loadPreviousBatches = useCallback(async () => {
    if (!user?.email) return;
    setLoadingPreviousBatches(true);
    setPreviousBatchesError("");
    try {
      const data = await TrainerAPI.getPreviousBatches();
      setPreviousBatchesState(data || []);
      setPreviousBatchesLoaded(true);
    } catch (err) {
      console.error("Error loading previous batches:", err);
      setPreviousBatchesError(err.message || "Failed to load previous batches");
    } finally {
      setLoadingPreviousBatches(false);
    }
  }, [user]);

  const handleTabChange = useCallback(
    (tab) => {
      setActiveTab(tab);
      setCurrentPage(1);
      if (tab === "previous" && !previousBatchesLoaded && !loadingPreviousBatches) {
        loadPreviousBatches();
      }
    },
    [previousBatchesLoaded, loadingPreviousBatches, loadPreviousBatches],
  );

  const fetchReferrals = useCallback(async () => {
    setLoadingReferrals(true);
    try {
      const res = await fetch(`${API_BASE}/trainer/referrals/redemptions`, {
        headers: getAuthHeaders(),
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.error || "Failed to load points");
      setTotalEarnedPoints(data.data?.earned_reward_points || 0);
    } catch (err) {
      console.error("Error fetching earned points:", err.message);
    } finally {
      setLoadingReferrals(false);
    }
  }, []);

  // Fetch the trainer's own referral history and build a lookup set of
  // batch_no values already referred, so the Refer button can be disabled.
  const fetchReferredBatches = useCallback(async () => {
    try {
      const res = await fetch(`${API_BASE}/trainer/referrals`, {
        headers: getAuthHeaders(),
      });
      const data = await res.json();
      if (!res.ok)
        throw new Error(data.error || "Failed to load referred batches");
      const batchNos = (data.data || []).map((r) => r.batch_no?.toString());
      setReferredBatchNos(new Set(batchNos));
    } catch (err) {
      console.error("Error fetching referred batches:", err.message);
    }
  }, []);

  useEffect(() => {
    fetchReferrals();
    fetchReferredBatches();
  }, [fetchReferrals, fetchReferredBatches]);

  // Debounced template search — fires only while the Refer modal is open
  useEffect(() => {
    if (!showReferForm) return;
    const q = templateSearch.trim();
    if (!q) {
      setTemplateResults([]);
      return;
    }
    const timer = setTimeout(async () => {
      setSearchingTemplates(true);
      setTemplateError("");
      try {
        const res = await fetch(
          `${API_BASE}/trainer/referrals/templates/search?q=${encodeURIComponent(q)}`,
          { headers: getAuthHeaders() },
        );
        const data = await res.json();
        if (!res.ok) throw new Error(data.error || "Search failed");
        setTemplateResults(data.data || []);
      } catch (err) {
        setTemplateError(err.message);
        setTemplateResults([]);
      } finally {
        setSearchingTemplates(false);
      }
    }, 400);
    return () => clearTimeout(timer);
  }, [templateSearch, showReferForm]);

  // Pre-fill Course Name whenever a (new) template is picked. Keyed on
  // selectedTemplate so the trainer's manual edits aren't overwritten when
  // the preview is toggled (that re-calls handleSelectTemplate with the same object).
  useEffect(() => {
    if (selectedTemplate?.fileName) {
      setCourseName(stripFileExtension(selectedTemplate.fileName));
    }
  }, [selectedTemplate]);

  const copyBatchResourceUrl = useCallback(async (batchNo) => {
    const url = `https://learn.thinkcloudly.com/batch-resource/?batch=${batchNo}`;
    try {
      await navigator.clipboard.writeText(url);
      toast.success("Resource link copied!");
    } catch (err) {
      toast.error("Failed to copy link");
    }
  }, []);

  const handleSelectBatch = useCallback(
    (batchItem) => {
      selectedBatchNoRef.current = batchItem.batch_no;
      setSelectedBatchNo(batchItem.batch_no);
      setSelectedBatchStudents(batchItem.students || []);
      // Store the clicked batch object directly, instead of re-finding it
      // by batch_no afterwards — that re-lookup only ever searched the
      // `batches` (current-tab) array, so opening this modal from the
      // Previous tab always came up empty and silently hid a previous
      // trainer's own MOU Hours / Attended Hours / Leaves / MOU Amount.
      setSelectedBatchDetails(batchItem);
      onBatchClick(batchItem);

      // Overlay with the latest TC_Batch_Journey snapshot once it loads.
      // It's saved fresh every time a session is marked, so it reflects
      // each student's status more accurately than TC_Batches.students
      // (rendered above as the immediate fallback while this is in flight).
      TrainerAPI.getBatchJourneyStatus(batchItem.batch_no)
        .then(({ students: journeyStudents }) => {
          // The trainer may have already clicked into a different batch by
          // the time this resolves — don't clobber that batch's students.
          if (
            selectedBatchNoRef.current !== batchItem.batch_no ||
            !journeyStudents?.length
          ) {
            return;
          }

          const journeyByEmail = new Map(
            journeyStudents.map((student) => [student.email, student])
          );

          setSelectedBatchStudents((prev) =>
            (prev || []).map((student) => {
              const journeyStudent = journeyByEmail.get(student.email);
              return journeyStudent
                ? { ...student, ...journeyStudent }
                : student;
            })
          );
        })
        .catch((err) => {
          // Non-fatal — the modal keeps showing the TC_Batches fallback.
          console.error("Failed to load journey status:", err);
        });
    },
    [onBatchClick],
  );

  const handleOpenReferForm = useCallback((batchItem) => {
    setActiveReferBatch(batchItem);
    setReferralDate(new Date().toISOString().split("T")[0]);
    setReferralTimestamp(new Date().toTimeString().slice(0, 5));
    setReferSuccessMsg("");
    setReferError("");
    // reset template search/preview state (Step 1)
    setTemplateSearch("");
    setTemplateResults([]);
    setSelectedTemplate(null);
    setCourseName("");
    setPreviewUrl("");
    setTemplateError("");
    setShowPreviewPanel(false);
    setShowReferForm(true);
  }, []);

  const handleSelectTemplate = useCallback(async (tpl) => {
    setSelectedTemplate(tpl);
    setPreviewUrl("");
    setShowPreviewPanel(false);
    setLoadingPreview(true);
    try {
      const res = await fetch(
        `${API_BASE}/trainer/referrals/templates/${encodeURIComponent(tpl.key)}/url?mode=preview`,
        { headers: getAuthHeaders() },
      );
      const data = await res.json();
      if (!res.ok) throw new Error(data.error || "Failed to load preview");
      setPreviewUrl(data.url);
    } catch (err) {
      toast.error(err.message || "Failed to load preview");
    } finally {
      setLoadingPreview(false);
    }
  }, []);

  const handleDownloadTemplate = useCallback(async (tpl) => {
    setDownloadingTemplate(true);
    try {
      const res = await fetch(
        `${API_BASE}/trainer/referrals/templates/${encodeURIComponent(tpl.key)}/url?mode=download`,
        { headers: getAuthHeaders() },
      );
      const data = await res.json();
      if (!res.ok) throw new Error(data.error || "Failed to download");
      const a = document.createElement("a");
      a.href = data.url;
      a.download = data.fileName || tpl.fileName;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
    } catch (err) {
      toast.error(err.message || "Download failed");
    } finally {
      setDownloadingTemplate(false);
    }
  }, []);

  // Fetch every template in the referral-template/ folder (empty query = all)
  const fetchAllTemplates = useCallback(async () => {
    setLoadingAllTemplates(true);
    setAllTemplatesError("");
    try {
      const res = await fetch(
        `${API_BASE}/trainer/referrals/templates/search?q=`,
        { headers: getAuthHeaders() },
      );
      const data = await res.json();
      if (!res.ok) throw new Error(data.error || "Failed to load templates");
      setAllTemplates(data.data || []);
    } catch (err) {
      setAllTemplatesError(err.message);
      setAllTemplates([]);
    } finally {
      setLoadingAllTemplates(false);
    }
  }, []);

  const handleOpenTemplatesModal = useCallback(() => {
    setShowTemplatesModal(true);
    fetchAllTemplates();
  }, [fetchAllTemplates]);

  // Picking a card in the grid popup reuses the existing select/preview flow
  const handlePickTemplateFromGrid = useCallback(
    (tpl) => {
      setShowTemplatesModal(false);
      handleSelectTemplate(tpl);
    },
    [handleSelectTemplate],
  );

  const handleCloseReferForm = useCallback(() => {
    setShowReferForm(false);
    setActiveReferBatch(null);
  }, []);

  const handleSubmitReferral = useCallback(async () => {
    if (!activeReferBatch) {
      setReferError("No batch selected");
      return;
    }
    if (!courseName.trim()) {
      toast.error("Please enter a course name");
      return;
    }
    setSubmittingReferral(true);
    setReferError("");
    try {
      const res = await fetch(`${API_BASE}/trainer/referrals`, {
        method: "POST",
        headers: getAuthHeaders(),
        body: JSON.stringify({
          batchNo: activeReferBatch.batch_no,
          batchStudents: activeReferBatch.students || [],
          referralDate,
          referralTimestamp,
          course: courseName.trim(),
        }),
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.error || "Failed to submit referral");
      setReferSuccessMsg("Referral submitted successfully — status: Pending");
      toast.success("Referral submitted successfully!");
      setShowReferForm(false);
      setActiveReferBatch(null);
      fetchReferrals();
      fetchReferredBatches();
    } catch (err) {
      setReferError(err.message);
      toast.error(err.message || "Failed to submit referral");
    } finally {
      setSubmittingReferral(false);
    }
  }, [
    activeReferBatch,
    referralDate,
    referralTimestamp,
    courseName,
    fetchReferrals,
    fetchReferredBatches,
  ]);

  const openUploadFor = useCallback((batchNo) => {
    setSelectedBatchForUpload(batchNo);
    setOpenUploadModal(true);
  }, []);

  const openViewFor = useCallback((batchNo) => {
    setSelectedBatchForView(batchNo);
    setOpenViewModal(true);
  }, []);

  // Re-fetches batches so newly-provisioned meeting links (or ones that
  // hadn't synced yet) show up without a full page reload.
  const handleRefreshBatches = useCallback(async () => {
    setRefreshing(true);
    try {
      const ok = await loadBatchesAndInvoices();
      if (ok) {
        toast.success("Batches refreshed");
      } else {
        toast.error("Couldn't refresh — showing previously loaded batches");
      }
    } finally {
      setRefreshing(false);
    }
  }, [loadBatchesAndInvoices]);

  // ─── Google Meet Join Handler ───
  const handleJoinBatch = useCallback(
    async (batchItem) => {
      const batchNo = batchItem.batch_no;
      if (joiningBatch === batchNo) return;

      // Open the window synchronously, still inside the click's user-gesture
      // window, and redirect it once we know the real link. Opening after the
      // awaits below expires the gesture and gets silently popup-blocked.
      const meetWindow = window.open(
        "",
        "google-meet-session",
        "width=1280,height=720,menubar=no,toolbar=no,location=yes,status=no,scrollbars=yes,resizable=yes",
      );
      if (meetWindow) {
        meetWindow.document.title = "Opening Google Meet…";
      }

      setJoiningBatch(batchNo);
      try {
        const batchDetails = await TrainerAPI.getBatchDetails(batchNo);
        const instructors = batchDetails?.instructors?.L || [];
        const myInstructor = instructors.find(
          (i) => i.M?.email?.S === user?.email,
        );

        const meetingLink =
          myInstructor?.M?.meetingLink?.S ||
          batchDetails?.meetingLink?.S ||
          batchItem.meetingLink ||
          "";

        if (!meetingLink) {
          toast.error("No Google Meet link found for this batch");
          if (meetWindow) meetWindow.close();
          setJoiningBatch(null);
          return;
        }

        const students =
          batchDetails?.students?.L?.map((s) => ({
            email: s.M?.email?.S || "",
            name: s.M?.name?.S || "",
          })) || [];

        const result = await TrainerAPI.joinBatchSession({
          batchNo,
          meetingLink,
          courseName: batchItem.courseName || batchDetails?.courseName?.S || "",
          admin: batchDetails?.admin?.S || "",
          trainerName: user?.name || "",
          studentDetails: students,
          gmAccountName:
            batchItem.gmAccountName || batchDetails?.gmAccountName?.S || "",
        });

        if (result.success) {
          toast.success(
            result.isExisting
              ? "Session already exists — opening Meet"
              : "Session created — opening Meet",
          );
          // The trainer may have closed the placeholder tab while the
          // request was in flight — .location.href would throw on a
          // closed window, so fall back to the manual link in that case.
          if (meetWindow && !meetWindow.closed) {
            meetWindow.location.href = result.meetingLink;
            meetWindow.focus();
          } else {
            // Popup was blocked (or closed) before we knew the link — give
            // the trainer a manual way in instead of failing silently.
            toast(
              (t) => (
                <span>
                  Popup blocked.{" "}
                  <a
                    href={result.meetingLink}
                    target="_blank"
                    rel="noopener noreferrer"
                    onClick={() => toast.dismiss(t.id)}
                    style={{ textDecoration: "underline" }}
                  >
                    Click here to join
                  </a>
                </span>
              ),
              { duration: 10000 },
            );
          }
        }
      } catch (err) {
        console.error("Join batch failed:", err);
        toast.error(err.message || "Failed to join session");
        if (meetWindow) meetWindow.close();
      } finally {
        setJoiningBatch(null);
      }
    },
    [joiningBatch, user],
  );

  return (
    <>
      <Toaster position="bottom-right" reverseOrder={false} />
      <div className="bg-gray-50 min-h-screen p-4">
        {/* Refer & Earn Banner — only for allowed_referral === "yes" */}
        {canRefer && (
          <ReferEarnBanner
            loadingReferrals={loadingReferrals}
            totalEarnedPoints={totalEarnedPoints}
            onOpenRedemptions={() => setShowRedemptionModal(true)}
          />
        )}

        {/* Main Card */}
        <div className="bg-white rounded-2xl shadow-sm border border-gray-200 p-5">
          {/* Header row */}
          <div className="flex items-center justify-between gap-3 mb-5">
            <div className="flex items-center gap-3">
              <button
                onClick={onBack}
                className="p-1.5 text-gray-500 hover:text-gray-800 hover:bg-gray-100 rounded-lg transition-colors"
                aria-label="Go back"
              >
                <ArrowLeft className="w-5 h-5" />
              </button>
              <div>
                <h3 className="text-lg font-bold text-gray-900">
                  All Batch Details
                </h3>
                <p className="text-xs text-gray-400 mt-0.5">
                  Total: {filteredSortedBatches.length} batches
                </p>
              </div>
            </div>

            <div className="flex items-center gap-2">
              <div className="relative">
                <Search className="w-3.5 h-3.5 text-gray-400 absolute left-3 top-1/2 -translate-y-1/2" />
                <input
                  type="text"
                  value={batchNoSearch}
                  onChange={handleBatchNoSearchChange}
                  placeholder="Filter by Batch No"
                  className="pl-8 pr-3 py-1.5 border border-gray-200 rounded-lg text-xs w-36 sm:w-48 focus:outline-none focus:ring-2 focus:ring-[#FC7A69]/30"
                />
              </div>

              <button
                onClick={
                  isPreviousTab ? loadPreviousBatches : handleRefreshBatches
                }
                disabled={
                  isPreviousTab
                    ? loadingPreviousBatches
                    : refreshing || loadingBatches
                }
                className="flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium rounded-lg border border-gray-200 text-gray-600 hover:bg-gray-50 hover:border-gray-300 transition-colors disabled:opacity-50 disabled:cursor-not-allowed whitespace-nowrap"
              >
                <RefreshCw
                  className={`w-3.5 h-3.5 ${(isPreviousTab ? loadingPreviousBatches : refreshing) ? "animate-spin" : ""}`}
                />
                {(isPreviousTab ? loadingPreviousBatches : refreshing)
                  ? "Refreshing..."
                  : "Refresh"}
              </button>
            </div>
          </div>

          {/* Current / Previous tabs */}
          <div className="flex items-center gap-1 mb-5 border-b border-gray-100">
            <button
              onClick={() => handleTabChange("current")}
              className={`px-4 py-2 text-sm font-medium border-b-2 -mb-px transition-colors ${
                activeTab === "current"
                  ? "border-[#FC7A69] text-[#221E45]"
                  : "border-transparent text-gray-500 hover:text-gray-700"
              }`}
            >
              Current Batches
            </button>
            <button
              onClick={() => handleTabChange("previous")}
              className={`px-4 py-2 text-sm font-medium border-b-2 -mb-px transition-colors ${
                activeTab === "previous"
                  ? "border-[#FC7A69] text-[#221E45]"
                  : "border-transparent text-gray-500 hover:text-gray-700"
              }`}
            >
              Previous Batches
              {previousBatchesLoaded && previousBatches.length > 0 && (
                <span className="ml-1.5 px-1.5 py-0.5 rounded-full text-[10px] font-semibold bg-gray-100 text-gray-600">
                  {previousBatches.length}
                </span>
              )}
            </button>
          </div>

          {referSuccessMsg && (
            <div className="mb-4 p-3 rounded-lg bg-green-50 text-green-700 text-sm flex items-center">
              <CheckCircle2 className="w-4 h-4 mr-2 shrink-0" />{" "}
              {referSuccessMsg}
            </div>
          )}
          {referError && (
            <div className="mb-4 p-3 rounded-lg bg-red-50 text-red-700 text-sm">
              {referError}
            </div>
          )}

          {/* Content */}
          {isPreviousTab && previousBatchesError ? (
            <div className="text-center py-16">
              <AlertCircle className="w-14 h-14 text-red-300 mx-auto mb-3" />
              <p className="text-red-500 text-sm mb-3">{previousBatchesError}</p>
              <button
                onClick={loadPreviousBatches}
                className="px-4 py-1.5 text-xs font-medium rounded-lg border border-gray-200 text-gray-600 hover:bg-gray-50"
              >
                Try again
              </button>
            </div>
          ) : (isPreviousTab ? loadingPreviousBatches : loadingBatches) ? (
            <div className="text-center py-16">
              <div className="inline-block animate-spin rounded-full h-8 w-8 border-b-2 border-[#FC7A69]" />
              <p className="text-gray-500 mt-4 text-sm">
                Loading batch details...
              </p>
            </div>
          ) : filteredSortedBatches.length === 0 ? (
            <div className="text-center py-16">
              <FileText className="w-14 h-14 text-gray-300 mx-auto mb-3" />
              <p className="text-gray-500 text-sm">
                {batchNoSearch.trim()
                  ? `No batches match "${batchNoSearch.trim()}"`
                  : isPreviousTab
                    ? "No previous batches — nothing you've been reassigned off of yet"
                    : "No batch details available"}
              </p>
            </div>
          ) : (
            <>
              {/* Mobile */}
              <div className="block lg:hidden">
                {currentBatches.map((batchItem, index) => (
                  <MobileBatchCard
                    key={batchItem.batch_no || index}
                    batchItem={batchItem}
                    onSelect={handleSelectBatch}
                    onUpload={() => openUploadFor(batchItem.batch_no)}
                    onView={() => openViewFor(batchItem.batch_no)}
                    onCopy={() => copyBatchResourceUrl(batchItem.batch_no)}
                    onRefer={handleOpenReferForm}
                    canRefer={canRefer}
                    joiningBatch={joiningBatch}
                    onJoin={handleJoinBatch}
                    isReferred={referredBatchNos.has(
                      batchItem.batch_no?.toString(),
                    )}
                    isPrevious={isPreviousTab}
                  />
                ))}
              </div>

              {/* Desktop */}
              <div className="hidden lg:block" style={{ overflow: "visible" }}>
                <table className="w-full border-collapse">
                  <thead>
                    <tr className="border-b-2 border-gray-100">
                      {[
                        "Batch No",
                        "Course Name",
                        "Batch Status",
                        "Start Date",
                        "Batch Timing",
                        "Invoice Status",
                        "Resources",
                      ]
                        .concat(canRefer ? ["Refer"] : [])
                        .map((h, i) => (
                          <th
                            key={i}
                            className="px-4 py-3 text-left text-xs font-semibold text-gray-700 bg-gray-50 first:rounded-tl-lg"
                          >
                            {h === "Resources" ? (
                              <div className="flex items-center gap-1.5">
                                <FolderOpen className="w-3.5 h-3.5 text-indigo-500" />
                                Resources
                              </div>
                            ) : (
                              h
                            )}
                          </th>
                        ))}
                      <th className="px-4 py-3 text-center text-xs font-semibold text-gray-700 bg-gray-50 rounded-tr-lg">
                        <div className="flex items-center justify-center gap-1.5">
                          <Video className="w-3.5 h-3.5 text-green-600" />
                          Action
                        </div>
                      </th>
                    </tr>
                  </thead>
                  <tbody>
                    {currentBatches.map((batchItem, index) => (
                      <DesktopRow
                        key={batchItem.batch_no || index}
                        batchItem={batchItem}
                        onRowClick={onBatchClick}
                        onSelect={handleSelectBatch}
                        onUpload={() => openUploadFor(batchItem.batch_no)}
                        onView={() => openViewFor(batchItem.batch_no)}
                        onCopy={() => copyBatchResourceUrl(batchItem.batch_no)}
                        onRefer={handleOpenReferForm}
                        canRefer={canRefer}
                        joiningBatch={joiningBatch}
                        onJoin={handleJoinBatch}
                        isReferred={referredBatchNos.has(
                          batchItem.batch_no?.toString(),
                        )}
                        isPrevious={isPreviousTab}
                      />
                    ))}
                  </tbody>
                </table>
              </div>
            </>
          )}

          {/* Pagination */}
          {totalPages > 1 &&
            !(isPreviousTab ? loadingPreviousBatches : loadingBatches) && (
              <div className="flex flex-col sm:flex-row justify-between items-center mt-6 gap-4 pt-4 border-t border-gray-100">
                <div className="text-sm text-gray-500">
                  Showing {startIndex + 1}–
                  {Math.min(endIndex, filteredSortedBatches.length)} of{" "}
                  {filteredSortedBatches.length} batches
                </div>
                <div className="flex items-center space-x-2">
                  <button
                    onClick={() => setCurrentPage((p) => p - 1)}
                    disabled={currentPage === 1}
                    className={`px-3 py-1.5 rounded-lg border text-sm font-medium ${currentPage === 1 ? "bg-gray-100 text-gray-400 cursor-not-allowed border-gray-100" : "bg-white text-gray-700 hover:bg-gray-50 border-gray-200"}`}
                  >
                    Previous
                  </button>
                  <div className="hidden sm:flex items-center space-x-1">
                    {Array.from({ length: totalPages }).map((_, index) => {
                      const pageNumber = index + 1;
                      return (
                        <button
                          key={pageNumber}
                          onClick={() => setCurrentPage(pageNumber)}
                          className={`w-8 h-8 rounded-lg border text-sm font-medium ${currentPage === pageNumber ? "bg-[#FC7A69] text-white border-[#FC7A69]" : "bg-white text-gray-700 hover:bg-gray-50 border-gray-200"}`}
                        >
                          {pageNumber}
                        </button>
                      );
                    })}
                  </div>
                  <div className="sm:hidden px-3 py-1.5 bg-gray-100 rounded-lg text-sm text-gray-600">
                    {currentPage} / {totalPages}
                  </div>
                  <button
                    onClick={() => setCurrentPage((p) => p + 1)}
                    disabled={currentPage === totalPages}
                    className={`px-3 py-1.5 rounded-lg border text-sm font-medium ${currentPage === totalPages ? "bg-gray-100 text-gray-400 cursor-not-allowed border-gray-100" : "bg-white text-gray-700 hover:bg-gray-50 border-gray-200"}`}
                  >
                    Next
                  </button>
                </div>
              </div>
            )}
        </div>
      </div>

      {/* Refer Form Modal — step-by-step */}
      {showReferForm && activeReferBatch && (
        <div className="fixed inset-0 bg-black/40 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-xl shadow-xl border border-gray-200 w-full max-w-md flex flex-col max-h-[95vh]">
            {/* Header */}
            <div className="flex items-center justify-between px-5 py-4 border-b border-gray-200 shrink-0">
              <div>
                <h2 className="text-sm font-semibold text-[#221E45]">
                  Refer a Student
                </h2>
                <p className="text-xs text-gray-500 mt-0.5">
                  Batch #{activeReferBatch.batch_no} —{" "}
                  {activeReferBatch.courseName}
                </p>
              </div>
              <button
                onClick={handleCloseReferForm}
                className="text-gray-400 hover:text-gray-600"
                aria-label="Close"
              >
                <X className="w-5 h-5" />
              </button>
            </div>

            {/* Steps — scrollable middle section between fixed header/footer */}
            <div className="overflow-y-auto min-h-0 flex-1 px-5 py-4 space-y-4">
              {/* Step 1 */}
              <div className="flex gap-3 items-start">
                <StepBadge n={1} />
                <div className="flex-1">
                  <div className="flex items-center justify-between mb-2">
                    <p className="text-sm font-semibold text-[#221E45]">
                      Download the Presentation
                    </p>
                    <button
                      onClick={handleOpenTemplatesModal}
                      className="flex items-center gap-1.5 px-2.5 py-1 text-[11px] font-medium rounded-lg border border-[#FC7A69]/40 text-[#FC7A69] hover:bg-[#FC7A69]/10 transition-colors"
                    >
                      <FolderOpen className="w-3.5 h-3.5" />
                      Get Templates
                    </button>
                  </div>

                  {!selectedTemplate ? (
                    <>
                      <div className="relative mb-2">
                        <Search className="w-3.5 h-3.5 text-gray-400 absolute left-3 top-1/2 -translate-y-1/2" />
                        <input
                          type="text"
                          value={templateSearch}
                          onChange={(e) => setTemplateSearch(e.target.value)}
                          placeholder="Search Course"
                          className="w-full pl-8 pr-3 py-2 border border-gray-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-[#FC7A69]/30"
                        />
                      </div>

                      {templateError && (
                        <p className="text-xs text-red-500 mb-2">
                          {templateError}
                        </p>
                      )}

                      {searchingTemplates && (
                        <div className="flex items-center gap-2 text-xs text-gray-400 py-2">
                          <Loader2 className="w-3.5 h-3.5 animate-spin" />{" "}
                          Searching…
                        </div>
                      )}

                      {!searchingTemplates &&
                        templateSearch.trim() &&
                        templateResults.length === 0 && (
                          <p className="text-xs text-gray-400 py-2">
                            No matching templates found.
                          </p>
                        )}

                      {!searchingTemplates && templateResults.length > 0 && (
                        <div className="border border-gray-200 rounded-lg divide-y divide-gray-100 max-h-32 overflow-y-auto">
                          {templateResults.map((tpl) => (
                            <button
                              key={tpl.key}
                              onClick={() => handleSelectTemplate(tpl)}
                              className="w-full text-left px-3 py-2 text-xs hover:bg-gray-50 transition-colors flex items-center gap-2 text-gray-700"
                            >
                              <FileText className="w-3.5 h-3.5 shrink-0" />
                              <span className="truncate">{tpl.fileName}</span>
                            </button>
                          ))}
                        </div>
                      )}
                    </>
                  ) : (
                    // Compact summary once a template is picked — keeps Step 1 short
                    // so the later steps don't get pushed far below the fold.
                    <div className="border border-gray-200 rounded-lg p-2.5">
                      <div className="flex items-center gap-2 mb-2">
                        <FileText className="w-3.5 h-3.5 text-[#FC7A69] shrink-0" />
                        <span className="text-xs font-medium text-gray-700 truncate flex-1">
                          {stripFileExtension(selectedTemplate.fileName)}
                        </span>
                        <button
                          onClick={() => {
                            setSelectedTemplate(null);
                            setPreviewUrl("");
                            setShowPreviewPanel(false);
                          }}
                          className="text-[11px] text-gray-400 hover:text-gray-600 underline shrink-0"
                        >
                          Change
                        </button>
                      </div>

                      <div className="flex items-center gap-2">
                        <button
                          onClick={() =>
                            handleDownloadTemplate(selectedTemplate)
                          }
                          disabled={downloadingTemplate}
                          className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-medium bg-[#221E45] text-white hover:bg-[#332b66] disabled:opacity-50 transition-colors"
                        >
                          {downloadingTemplate ? (
                            <Loader2 className="w-3.5 h-3.5 animate-spin" />
                          ) : (
                            <Download className="w-3.5 h-3.5" />
                          )}
                          Download
                        </button>

                        {selectedTemplate.fileName
                          .toLowerCase()
                          .endsWith(".pdf") && (
                          <button
                            onClick={async () => {
                              const next = !showPreviewPanel;
                              setShowPreviewPanel(next);
                              if (next && !previewUrl)
                                await handleSelectTemplate(selectedTemplate);
                            }}
                            className="text-[11px] text-gray-500 hover:text-gray-700 underline"
                          >
                            {showPreviewPanel ? "Hide preview" : "Show preview"}
                          </button>
                        )}
                      </div>
                      {showPreviewPanel &&
                        (loadingPreview ? (
                          <div className="flex items-center gap-2 text-xs text-gray-400 py-3">
                            <Loader2 className="w-3.5 h-3.5 animate-spin" />{" "}
                            Loading preview…
                          </div>
                        ) : previewUrl ? (
                          <>
                            {getCourseLogo(
                              activeReferBatch?.courseName,
                              selectedTemplate?.fileName,
                            ) && (
                              <div className="flex items-center gap-2 mt-2 mb-1 px-2 py-1.5 bg-gray-50 rounded-lg border border-gray-100">
                                <img
                                  src={getCourseLogo(
                                    activeReferBatch?.courseName,
                                    selectedTemplate?.fileName,
                                  )}
                                  alt={`${activeReferBatch?.courseName || "Course"} logo`}
                                  className="w-8 h-8 rounded-md shrink-0"
                                />
                                <span className="text-xs font-medium text-gray-600 truncate">
                                  {activeReferBatch?.courseName}
                                </span>
                              </div>
                            )}
                            <iframe
                              src={previewUrl}
                              title="Template preview"
                              className="w-full h-48 rounded-lg border border-gray-100"
                            />
                          </>
                        ) : null)}
                    </div>
                  )}
                </div>
              </div>

              <div className="flex gap-2 items-center px-1">
                <div className="h-px flex-1 bg-gray-100" />
                <ChevronRight className="w-3.5 h-3.5 text-gray-300 shrink-0" />
                <div className="h-px flex-1 bg-gray-100" />
              </div>

              {/* Step 2 */}
              <div className="flex gap-3 items-start">
                <StepBadge n={2} />
                <div className="flex-1">
                  <p className="text-sm font-semibold text-[#221E45] mb-1">
                    Show the Presentation to the Students
                  </p>
                  <p className="text-xs text-gray-500">
                    Walk the student through the presentation slides and explain
                    the benefits of our courses.
                  </p>
                </div>
              </div>

              <div className="flex gap-2 items-center px-1">
                <div className="h-px flex-1 bg-gray-100" />
                <ChevronRight className="w-3.5 h-3.5 text-gray-300 shrink-0" />
                <div className="h-px flex-1 bg-gray-100" />
              </div>

              {/* Step 3 — existing referral form */}
              <div className="flex gap-3 items-start">
                <StepBadge n={3} />
                <div className="flex-1 space-y-4">
                  <p className="text-sm font-semibold text-[#221E45]">
                    New Referral
                  </p>

                  <div>
                    <label className="flex items-center text-xs font-medium text-gray-600 mb-1">
                      <Users className="w-3.5 h-3.5 mr-1" /> Batch Students (
                      {activeReferBatch.students?.length || 0})
                    </label>
                    <div className="bg-gray-50 border border-gray-200 rounded-lg p-3 max-h-32 overflow-y-auto">
                      {activeReferBatch.students?.length ? (
                        <ul className="text-sm text-gray-600 space-y-1">
                          {activeReferBatch.students.map((s, idx) => (
                            <li key={idx}>{s.name}</li>
                          ))}
                        </ul>
                      ) : (
                        <p className="text-sm text-gray-400">
                          No students found for this batch
                        </p>
                      )}
                    </div>
                  </div>

                  <div>
                    <label className="flex items-center text-xs font-medium text-gray-600 mb-1">
                      <Calendar className="w-3.5 h-3.5 mr-1" /> Referral Date
                    </label>
                    <input
                      type="date"
                      value={referralDate}
                      onChange={(e) => setReferralDate(e.target.value)}
                      className="w-full px-3 py-2.5 border border-gray-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-[#FC7A69]/30"
                    />
                  </div>

                  <div>
                    <label className="flex items-center text-xs font-medium text-gray-600 mb-1">
                      <Clock className="w-3.5 h-3.5 mr-1" /> Referral Time
                    </label>
                    <input
                      type="time"
                      value={referralTimestamp}
                      onChange={(e) => setReferralTimestamp(e.target.value)}
                      className="w-full px-3 py-2.5 border border-gray-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-[#FC7A69]/30"
                    />
                  </div>
                </div>
              </div>

              <div className="flex gap-2 items-center px-1">
                <div className="h-px flex-1 bg-gray-100" />
                <ChevronRight className="w-3.5 h-3.5 text-gray-300 shrink-0" />
                <div className="h-px flex-1 bg-gray-100" />
              </div>

              {/* Step 4 — Course Name (pre-filled from template, editable) */}
              <div className="flex gap-3 items-start">
                <StepBadge n={4} />
                <div className="flex-1">
                  <p className="text-sm font-semibold text-[#221E45] mb-2">
                    Course Name
                  </p>
                  <input
                    type="text"
                    value={courseName}
                    onChange={(e) => setCourseName(e.target.value)}
                    placeholder="Select a template or enter course name"
                    className="w-full px-3 py-2.5 border border-gray-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-[#FC7A69]/30"
                  />
                </div>
              </div>
            </div>

            {/* Footer — Submit button */}
            <div className="px-5 py-4 border-t border-gray-100 shrink-0">
              <button
                onClick={handleSubmitReferral}
                disabled={submittingReferral}
                className="w-full flex items-center justify-center px-4 py-2.5 bg-[#FC7A69] text-white text-sm font-medium rounded-lg hover:bg-[#fb6a57] disabled:opacity-60 transition-all"
              >
                {submittingReferral && (
                  <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                )}
                {submittingReferral ? "Submitting..." : "Submit Referral"}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* "Get Templates" grid popup — all PPT templates */}
      <TemplatesModal
        isOpen={showTemplatesModal}
        onClose={() => setShowTemplatesModal(false)}
        templates={allTemplates}
        loading={loadingAllTemplates}
        error={allTemplatesError}
        onSelect={handlePickTemplateFromGrid}
      />

      {/* Available Redemptions Modal — real API data */}
      <RedemptionModal
        isOpen={showRedemptionModal}
        onClose={() => setShowRedemptionModal(false)}
      />

      {/* Invoice Modal */}
      {selectedBatchForInvoice && (
        <RaiseInvoiceModal
          isOpen={!!selectedBatchForInvoice}
          batch={{
            ...selectedBatchForInvoice,
            instructor:
              selectedBatchForInvoice.instructor?.filter(
                (trainer) => trainer.email === user?.email,
              ) || [],
          }}
          trainerEmail={user?.email}
          onClose={() => setSelectedBatchForInvoice(null)}
          onSuccess={() => {
            toast.success("Invoice submitted successfully!");
            setSelectedBatchForInvoice(null);
            loadBatchesAndInvoices();
          }}
        />
      )}

      {selectedBatchStudents && (
        <StudentClassesModal
          batchNo={selectedBatchNo}
          students={selectedBatchStudents}
          instructorDetails={selectedBatchDetails || {}}
          onClose={() => {
            setSelectedBatchStudents(null);
            setSelectedBatchNo("");
            setSelectedBatchDetails(null);
          }}
        />
      )}

      <UploadResourceModal
        isOpen={openUploadModal}
        batchNo={selectedBatchForUpload}
        onClose={() => setOpenUploadModal(false)}
        onUpload={() => console.log("refresh resource list if needed")}
      />

      <ViewResourcesModal
        isOpen={openViewModal}
        batchNo={selectedBatchForView}
        onClose={() => setOpenViewModal(false)}
      />
    </>
  );
};

export default BatchTableView;